import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Row, Col, Space, Modal, Input, Alert } from 'antd';
import { TemplateManipulateProps } from '../../models/templateManipulate';
import { Channels } from './channelsEnum';
import { Components } from './componentsEnum';
import { OtherList } from './otherListEnum';
import DeleteCardModal from './deleteCardModal';
import { api } from '../../utils/axiosConfig';
import { Template } from '../../interfaces/backend/Template';
import stripDisallowedIdCharacters from '../../utils/stripDisallowedIdCharacters';
import { TemplatesPOSTRequest } from '../../interfaces/backend/TemplatesPOSTRequest';
import { AxiosError } from 'axios';
import { Notification } from '../../interfaces/backend/Notification';
import { NotificationPOSTRequest } from '../../interfaces/backend/NotificationPOSTRequest';
import { TemplatesPATCHDefaultRequest } from '../../interfaces/backend/TemplatesPATCHDefaultRequest';

const TemplateManipulate: React.FC<TemplateManipulateProps> = ({
  onCopy,
  onDelete,
  onMakeDefault,
  editPath,
  editButtonText = 'Edit Design',
  cardType,
  template,
  changeLoadingState,
  setCardLoading
}): JSX.Element => {
  const [showDeleteModal, changeShowDeleteModal] = useState<boolean>();
  const [templateBeingCopied, setTemplateBeingCopied] = useState<Template>();
  const [notificationBeingCopied, setNotificationBeingCopied] =
    useState<Notification>();
  const [newCopyId, setNewCopyId] = useState('');
  const [copyError, setCopyError] = useState('');
  const [copyAPIsRunning, setCopyAPIsRunning] = useState(false);

  const copyObject = () => {
    if (templateBeingCopied) copyTemplate();
    else if (notificationBeingCopied) copyNotification();
  };

  const copyTemplate = async () => {
    if (!templateBeingCopied) return;

    setCopyAPIsRunning(true);
    const requestBody: TemplatesPOSTRequest = {
      ...{
        ...templateBeingCopied,
        envId: undefined, // remove params that shouldn't go to the request body
        notificationId: undefined,
        default: undefined,
        channel: undefined
      },
      templateId: newCopyId
    };
    api(
      'POST',
      `/${templateBeingCopied.envId}/notifications/${templateBeingCopied.notificationId}/templates/${templateBeingCopied.channel}`,
      requestBody
    )
      .then(() => {
        setCopyAPIsRunning(false);
        setTemplateBeingCopied(undefined);
        setCopyError('');
        setNewCopyId('');
        onCopy();
      })
      .catch((e) => {
        setCopyAPIsRunning(false);
        const error = e as AxiosError;
        if (
          error.response?.data.message &&
          error.response?.data.message.includes('duplicate templateId')
        )
          setCopyError('There is already a template with this ID.');
        else throw e;
      });
  };

  const copyNotification = async () => {
    if (!notificationBeingCopied) return;
    const envId = notificationBeingCopied.envId;

    setCopyAPIsRunning(true);
    const requestBody: NotificationPOSTRequest = {
      notificationId: newCopyId,
      title: notificationBeingCopied.title + ' (copy)',
      channels: notificationBeingCopied.channels
    };

    api('POST', `/${envId}/notifications`, requestBody)
      .then(async () => {
        // now we need to copy all the templates of the original notification into the new notification
        const templatesToCopy: Template[] = [];
        await Promise.all(
          Object.values(Channels).map(async (c) => {
            const templates = (
              await api(
                'GET',
                `/${envId}/notifications/${notificationBeingCopied.notificationId}/templates/${c}`
              )
            ).data as Template[];
            templates.map((t) => templatesToCopy.push(t));
          })
        );

        await Promise.all(
          templatesToCopy.map((t) => {
            const request: TemplatesPOSTRequest = {
              ...{
                ...t,
                envId: undefined, // remove params that shouldn't go to the request body
                notificationId: undefined,
                default: undefined,
                channel: undefined
              }
            };
            return api(
              'POST',
              `/${envId}/notifications/${newCopyId}/templates/${t.channel}`,
              request
            );
          })
        );

        // the first templates that is copied above becomes default, so we have to set default explicitly
        // set default templates
        await Promise.all(
          templatesToCopy
            .filter((c) => c.default)
            .map((t) => {
              const request: TemplatesPATCHDefaultRequest = {
                templateId: t.templateId
              };
              return api(
                'PATCH',
                `/${envId}/notifications/${newCopyId}/defaulttemplate/${t.channel}`,
                request
              );
            })
        );

        setCopyAPIsRunning(false);
        setNotificationBeingCopied(undefined);
        setCopyError('');
        setNewCopyId('');
        onCopy();
      })
      .catch((e) => {
        setCopyAPIsRunning(false);
        const error = e as AxiosError;
        if (
          error.response?.data.message &&
          error.response?.data.message.includes('duplicate notificationId')
        )
          setCopyError('There is already a notification with this ID.');
        else throw e;
      });
  };

  const onClickDelete = (): void => {
    setCardLoading(true);
    if (cardType !== Components.EMAIL) {
      onDelete();
    } else {
      setCardLoading(false);
      changeShowDeleteModal(true);
    }
  };

  return (
    <>
      <Modal
        title="Duplicating"
        open={templateBeingCopied || notificationBeingCopied ? true : false}
        onOk={copyObject}
        onCancel={() => {
          setTemplateBeingCopied(undefined);
          setNotificationBeingCopied(undefined);
        }}
        okButtonProps={{
          disabled: !newCopyId,
          loading: copyAPIsRunning
        }}
      >
        New {templateBeingCopied ? 'Template' : 'Notification'} ID (cannot be
        changed later):
        <Input
          value={newCopyId}
          onChange={(e) =>
            setNewCopyId(stripDisallowedIdCharacters(e.target.value))
          }
        />
        {copyError && (
          <Alert
            style={{ marginTop: '8px' }}
            message={copyError}
            type="error"
          />
        )}
      </Modal>
      <Col span={4}>
        <Row justify="end">
          <Space align="end" direction="vertical">
            {cardType !== Components.EMAIL &&
              cardType !== Channels.SMS &&
              cardType !== Channels.CALL &&
              cardType !== Channels.PUSH &&
              cardType !== Channels.WEB_PUSH &&
              editPath !== undefined && (
                <Link to={editPath}>
                  <Button
                    data-cy={'Edit_Design'}
                    key="1"
                    type="primary"
                    style={{ width: '150px' }}
                  >
                    {editButtonText}
                  </Button>
                </Link>
              )}
            {cardType !== Components.EMAIL && (
              <Button
                key="copy"
                type="text"
                onClick={() => {
                  if (!Object.keys(template).includes('templateId'))
                    setNotificationBeingCopied(template as Notification);
                }}
              >
                Duplicate
              </Button>
            )}

            <>
              {!(
                cardType === Components.EMAIL ||
                cardType === OtherList.Notification
              ) &&
                onMakeDefault !== undefined && (
                  <Button
                    key="3"
                    data-cy="Promote_To_Default"
                    type="text"
                    onClick={(): void => {
                      onMakeDefault();
                    }}
                  >
                    Promote to default
                  </Button>
                )}

              <Button
                key="2"
                data-cy="Delete_Template"
                type="text"
                danger
                onClick={onClickDelete}
              >
                Delete
              </Button>
            </>
          </Space>
        </Row>
      </Col>
      <DeleteCardModal
        cardType={cardType}
        showDeleteModal={showDeleteModal as boolean}
        onDelete={onDelete}
        changeShowDeleteModal={
          changeShowDeleteModal as React.Dispatch<React.SetStateAction<boolean>>
        }
        setCardLoading={setCardLoading}
        changeLoadingState={changeLoadingState}
      />
    </>
  );
};
export default TemplateManipulate;
